import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { toast } from "sonner";
import { CheckCircle2, Eye, Navigation, Phone, Route as RouteIcon, Search } from "lucide-react";
import PageHeader, { card, ghostBtn } from "@/app/PageHeader";
import { useSession } from "@/app/session";

// Plan a day of walk-in sales visits: pick leads, get them in the order with the fewest miles, and
// open the drive in Google Maps (free links). Stops are ordered by the backend (crm.py /visits/plan).

const START_KEY = "vortex_visit_start";
const PLAN_KEY = "vortex_visit_plan";
const read = (key, fallback) => {
  try { return JSON.parse(localStorage.getItem(key)) ?? fallback; } catch { return fallback; }
};
const write = (key, value) => {
  try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* storage blocked */ }
};

const field = "h-10 w-full rounded-md border border-white/10 bg-vortex-ink/60 px-3 text-sm text-slate-100 focus:outline-none focus:ring-2 focus:ring-vortex-cyan";
const label = "font-mono text-[10px] tracking-[0.25em] text-slate-400 uppercase";
const btnPrimary = "flex items-center justify-center gap-2 rounded-full bg-vortex-cyan px-6 py-3 font-mono text-xs tracking-[0.2em] uppercase text-vortex-ink transition-all duration-300 hover:shadow-[0_0_30px_rgba(92,179,226,0.4)] disabled:opacity-50";
const PRIORITY = { A: "text-emerald-300", B: "text-amber-300", C: "text-slate-500" };

const pin = (text, color) => L.divIcon({
  className: "",
  html: `<div style="background:${color};color:#050814;width:28px;height:28px;border-radius:50%;display:flex;align-items:center;justify-content:center;font:700 12px 'DM Sans',sans-serif;border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,.4)">${text}</div>`,
  iconSize: [28, 28], iconAnchor: [14, 14],
});

const VisitMap = ({ plan, visited }) => {
  const ref = useRef(null);
  const mapRef = useRef(null);
  useEffect(() => {
    if (!ref.current || !plan) return;
    if (!mapRef.current) {
      mapRef.current = L.map(ref.current, { scrollWheelZoom: false });
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { attribution: "© OpenStreetMap", maxZoom: 19 })
        .addTo(mapRef.current);
    }
    const map = mapRef.current;
    map.eachLayer((l) => { if (!(l instanceof L.TileLayer)) map.removeLayer(l); });
    const pts = [[plan.start.lat, plan.start.lng]];
    L.marker(pts[0], { icon: pin("S", "#F8FAFC") }).addTo(map).bindPopup("Start");
    plan.stops.forEach((s, i) => {
      L.marker([s.lat, s.lng], { icon: pin(i + 1, visited[s.id] ? "#34d399" : "#5CB3E2") }).addTo(map).bindPopup(s.company);
      pts.push([s.lat, s.lng]);
    });
    if (plan.round_trip) pts.push(pts[0]);
    L.polyline(pts, { color: "#5CB3E2", weight: 3, opacity: 0.8, dashArray: "6" }).addTo(map);
    map.fitBounds(pts, { padding: [30, 30], maxZoom: 13 });
    setTimeout(() => map.invalidateSize(), 150);
  }, [plan, visited]);
  useEffect(() => () => { mapRef.current?.remove(); mapRef.current = null; }, []);
  return <div ref={ref} className="h-[360px] w-full overflow-hidden rounded-md border border-white/10" data-testid="visit-map" />;
};

const Visits = () => {
  const { api, onAuthError } = useSession();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [picked, setPicked] = useState({});
  const [start, setStart] = useState(() => read(START_KEY, ""));
  const [roundTrip, setRoundTrip] = useState(true);
  const [plan, setPlan] = useState(() => read(PLAN_KEY, null));
  const [visited, setVisited] = useState(() => read(PLAN_KEY, null)?.visited || {});
  const [planning, setPlanning] = useState(false);

  useEffect(() => {
    api.get("/crm/leads")
      .then((res) => setLeads(res.data.filter((l) => !["won", "lost"].includes(l.status))))
      .catch((err) => onAuthError(err, "Could not load leads."))
      .finally(() => setLoading(false));
  }, [api, onAuthError]);

  const shown = useMemo(() => {
    const term = q.trim().toLowerCase();
    return leads.filter((l) => !term || [l.company, l.city, l.contact_name].some((v) => (v || "").toLowerCase().includes(term)));
  }, [leads, q]);
  const chosen = Object.keys(picked).filter((id) => picked[id]);
  const hasPlace = (l) => l.lat != null || l.address || l.city || l.postal_code;

  const planRoute = async () => {
    if (!start.trim()) { toast.error("Enter where you're starting from."); return; }
    setPlanning(true);
    write(START_KEY, start);
    try {
      const res = await api.post("/crm/visits/plan", { lead_ids: chosen, start, round_trip: roundTrip });
      setPlan(res.data);
      setVisited({});
      write(PLAN_KEY, { ...res.data, visited: {} });
      if (res.data.missing.length) toast(`Couldn't place ${res.data.missing.map((m) => m.company).join(", ")} on the map. Add a street address to include them.`);
    } catch (err) {
      onAuthError(err, "Couldn't plan the route.");
    } finally {
      setPlanning(false);
    }
  };

  const markVisited = useCallback(async (stop) => {
    try {
      await api.post(`/crm/leads/${stop.id}/activities`, { type: "visit", text: "Walk-in visit (visit route)" });
      setVisited((v) => {
        const next = { ...v, [stop.id]: true };
        write(PLAN_KEY, { ...plan, visited: next });
        return next;
      });
      toast.success(`Logged a visit to ${stop.company}.`);
    } catch (err) {
      onAuthError(err, "Couldn't log the visit.");
    }
  }, [api, onAuthError, plan]);

  return (
    <div data-testid="visits-page">
      <PageHeader title="Visit Routes"
        intro="Pick the leads you want to drop in on, and get them in the order that drives the fewest miles. Opening the route in Google Maps is free." />

      <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
        <div className={`${card} p-5 lg:col-span-5`}>
          <div className="space-y-2">
            <label className={label} htmlFor="visit-start">Start from</label>
            <input id="visit-start" className={field} value={start} onChange={(e) => setStart(e.target.value)} placeholder="Your address, e.g. 100 W Gay St, West Chester, PA" />
          </div>
          <label className="mt-3 flex items-center gap-2 text-xs text-slate-300">
            <input type="checkbox" className="accent-[#5CB3E2]" checked={roundTrip} onChange={(e) => setRoundTrip(e.target.checked)} />
            End back at the start
          </label>

          <div className="mt-5 flex items-center gap-2">
            <Search size={14} className="text-slate-500" />
            <input className={field} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter leads by name or town" />
          </div>
          <div className="mt-3 max-h-[420px] overflow-y-auto border border-white/10" data-testid="visit-leads">
            {loading ? <p className="p-4 text-sm text-slate-500">Loading leads...</p>
              : shown.length === 0 ? <p className="p-4 text-sm text-slate-500">No open leads. Add some on the <Link to="/admin/crm" className="text-vortex-cyan hover:underline">Leads</Link> page.</p>
              : shown.map((l) => (
                <label key={l.id} className="flex cursor-pointer items-start gap-3 border-b border-white/5 px-4 py-3 hover:bg-vortex-surface/40">
                  <input type="checkbox" className="mt-1 accent-[#5CB3E2]" checked={!!picked[l.id]} onChange={(e) => setPicked({ ...picked, [l.id]: e.target.checked })} />
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-2">
                      <span className="truncate text-sm text-slate-100">{l.company}</span>
                      <span className={`font-mono text-[10px] ${PRIORITY[l.priority] || ""}`}>{l.priority}</span>
                    </span>
                    <span className="block truncate text-xs text-slate-500">
                      {[l.address || l.city, l.status].filter(Boolean).join(" · ")}
                      {!hasPlace(l) && <span className="text-amber-300"> · no address</span>}
                    </span>
                  </span>
                </label>
              ))}
          </div>
          <button className={`${btnPrimary} mt-5 w-full`} onClick={planRoute} disabled={planning || !chosen.length} data-testid="visit-plan-button">
            <RouteIcon size={14} /> {planning ? "Planning..." : `Plan route for ${chosen.length} stop${chosen.length === 1 ? "" : "s"}`}
          </button>
          <p className="mt-2 text-xs text-slate-500">The first route to a new address can take a few seconds while it's placed on the map; after that it's remembered.</p>
        </div>

        <div className="lg:col-span-7">
          {!plan ? (
            <div className={`${card} p-10 text-center text-sm text-slate-500`}>Your route shows here.</div>
          ) : (
            <div className="space-y-5" data-testid="visit-plan">
              <VisitMap plan={plan} visited={visited} />
              <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="text-sm text-slate-300">
                  {plan.stops.length} stops · about {plan.straight_line_miles} mi as the crow flies
                  <span className="text-slate-500"> (road miles run a bit higher)</span>
                </p>
                <div className="flex flex-wrap gap-2">
                  {plan.maps_links.map((url, i) => (
                    <a key={url} href={url} target="_blank" rel="noreferrer" className={ghostBtn}>
                      <Navigation size={12} /> {plan.maps_links.length > 1 ? `Drive part ${i + 1}` : "Open in Google Maps"}
                    </a>
                  ))}
                </div>
              </div>
              {plan.maps_links.length > 1 && (
                <p className="text-xs text-slate-500">Google Maps on a phone takes up to 5 stops per link, so the drive is split into parts. Open each part when you finish the last.</p>
              )}
              <ol className="border border-white/10">
                {plan.stops.map((s, i) => (
                  <li key={s.id} className="flex items-start gap-4 border-b border-white/5 px-5 py-4">
                    <span className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold text-vortex-ink ${visited[s.id] ? "bg-emerald-400" : "bg-vortex-cyan"}`}>{i + 1}</span>
                    <div className="min-w-0 flex-1">
                      <Link to={`/admin/crm?lead=${s.id}`} className="text-sm font-medium text-slate-100 hover:text-vortex-cyan">{s.company}</Link>
                      <p className="text-xs text-slate-500">{[s.address || s.city, s.contact_name && `ask for ${s.contact_name}`].filter(Boolean).join(" · ")}</p>
                      <div className="mt-1 flex flex-wrap gap-4 text-xs">
                        <span className="text-slate-500">{s.leg_miles} mi from the last stop</span>
                        {s.phone && <a href={`tel:${s.phone.replace(/[^\d+]/g, "")}`} className="flex items-center gap-1 text-vortex-cyan hover:underline"><Phone size={11} />{s.phone}</a>}
                        <a href={`https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${s.lat},${s.lng}`} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-vortex-cyan hover:underline"><Eye size={11} /> Street View</a>
                      </div>
                    </div>
                    {visited[s.id] ? (
                      <span className="flex items-center gap-1 text-xs text-emerald-300"><CheckCircle2 size={14} /> Visited</span>
                    ) : (
                      <button className={ghostBtn} onClick={() => markVisited(s)}>Mark visited</button>
                    )}
                  </li>
                ))}
              </ol>
              {plan.missing.length > 0 && (
                <p className="text-xs text-amber-300">Not on the route (no address found): {plan.missing.map((m) => m.company).join(", ")}</p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Visits;
